import { Button, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';

import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import { useModalControl } from 'hooks/useModalControl';
import { saudiRewardsRetroRedeemMatchingOrdersQuery } from 'queries/saudi-rewards-retro-redeem/queries';
import { useRetroRedeemSaudiRewardsPoints } from 'queries/saudi-rewards-retro-redeem/useRetroRedeemSaudiRewardsPoints';

import ContactDialog from 'components/@common/ContactDialog';
import { useSessionRedeemToken } from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer/hooks';
import StickyButtonContainer from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer/StickyButtonContainer';
import { StepData } from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer/types';
import { useMultiStep } from 'components/@step-flow/MultiStep';

import OrderList from './OrderList';
import OrdersErrorState from './OrdersErrorState';

const Orders = () => {
  const { push } = useRouter();
  const redeemToken = useSessionRedeemToken();
  const multistep = useMultiStep<StepData>();
  const {
    open: openContact,
    onClose: onCloseContact,
    onOpen: onOpenContact,
  } = useModalControl();
  const { locale } = useLocale();
  const { data, isLoading: isRedeemOrdersLoading } = useQuery(
    saudiRewardsRetroRedeemMatchingOrdersQuery({ locale }),
  );

  const {
    redeemPoints,
    isLoading: isRedeemPointsLoading,
    error,
    reset,
  } = useRetroRedeemSaudiRewardsPoints();

  const handleRetry = () => {
    reset();
  };

  const handleSubmit = () => {
    if (!redeemToken) {
      push(Routes.Notfound);
      return;
    }

    redeemPoints(
      { redeemToken },
      {
        onSuccess: () => multistep.proceed(),
      },
    );
  };

  return (
    <>
      {error ? (
        <OrdersErrorState
          onRetry={handleRetry}
          onContactSupport={onOpenContact}
        />
      ) : (
        <Stack sx={{ flexGrow: { xs: 1, md: 0 } }}>
          <Typography variant="h6">
            <FormattedMessage id="claim.points.orders.title" />
          </Typography>
          <Typography variant="small" sx={{ mb: 2 }}>
            <FormattedMessage id="claim.points.orders.description" />
          </Typography>
          {!!data?.orders?.length && (
            <OrderList
              isLoading={isRedeemOrdersLoading}
              orders={data?.orders}
            />
          )}
          <StickyButtonContainer>
            <Stack sx={{ gap: 3 }}>
              <Button
                loading={isRedeemPointsLoading}
                disabled={isRedeemOrdersLoading}
                onClick={handleSubmit}
                variant="outlined"
                color="secondary"
                sx={{ width: { xs: '100%', md: 'fit-content' } }}
              >
                <FormattedMessage id="claim.points.orders.confirm" />
              </Button>
              <Button variant="text" onClick={onOpenContact}>
                <FormattedMessage id="claim.points.orders.contact_support" />
              </Button>
            </Stack>
          </StickyButtonContainer>
        </Stack>
      )}
      <ContactDialog open={openContact} onClose={onCloseContact} />
    </>
  );
};

export default Orders;
